import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import type { LeadTab, ShootTab } from './useShootTab';

/**
 * The shoot view's tab strip (mock-up §04): the editor's mobile Script /
 * Scratch switch, in mono with counts, parked under the page header so it
 * stays reachable while the questions scroll. The left half is the brief's
 * messaging or, for a script that also carries legacy questions, the script.
 */

const LEAD_LABELS: Record<LeadTab, string> = { brief: 'Brief', script: 'Script' };

interface StickyProps {
  /** The page's sticky header, measured so the strip parks directly under it.
   *  Left out when nothing above is sticky (inside a bundle the switcher bar
   *  owns the top of the viewport), in which case the strip scrolls away. */
  stickyUnder?: RefObject<HTMLElement | null>;
  /** Parks the strip at the very top instead: the call-sheet embed drops the
   *  page header, so nothing sits above the strip inside the frame. */
  stickyAtTop?: boolean;
}

interface Props extends StickyProps {
  active: ShootTab;
  onChange: (tab: ShootTab) => void;
  /** Which tab sits on the left. Briefs lead with the messaging. */
  lead?: LeadTab;
  leadCount: number;
  questionCount: number;
}

/** Header heights differ by route and change when a banner opens inside one. */
function useStickyTop(ref: RefObject<HTMLElement | null> | undefined): number | null {
  const [top, setTop] = useState<number | null>(null);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const measure = () => setTop(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return top;
}

/** The strip's backdrop and sticky parking, shared with the script filters. */
export function ShootStrip({
  stickyUnder,
  stickyAtTop,
  className = '',
  children,
}: StickyProps & { className?: string; children: ReactNode }) {
  const measured = useStickyTop(stickyAtTop ? undefined : stickyUnder);
  const top = stickyAtTop ? 0 : measured;
  return (
    <div
      className={`${top === null ? '' : 'sticky z-10 '}bg-page/95 backdrop-blur py-3 ${className}`}
      style={top === null ? undefined : { top }}
    >
      {children}
    </div>
  );
}

export default function ShootTabs({
  active,
  onChange,
  lead = 'brief',
  leadCount,
  questionCount,
  stickyUnder,
  stickyAtTop,
}: Props) {
  const briefRef = useRef<HTMLButtonElement>(null);
  const questionsRef = useRef<HTMLButtonElement>(null);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = e.key === 'ArrowLeft' ? lead : 'questions';
    onChange(next);
    (next === lead ? briefRef : questionsRef).current?.focus();
  }

  return (
    <ShootStrip stickyUnder={stickyUnder} stickyAtTop={stickyAtTop}>
      <div
        role="tablist"
        aria-label="Shoot view"
        onKeyDown={handleKeyDown}
        className="flex gap-0.5 p-0.5 rounded-full bg-elevated border border-line"
      >
        <TabHalf
          buttonRef={briefRef}
          label={LEAD_LABELS[lead]}
          count={leadCount}
          tab={lead}
          on={active === lead}
          onClick={() => onChange(lead)}
        />
        <TabHalf
          buttonRef={questionsRef}
          label="Questions"
          count={questionCount}
          tab="questions"
          on={active === 'questions'}
          onClick={() => onChange('questions')}
        />
      </div>
    </ShootStrip>
  );
}

function TabHalf({
  buttonRef,
  label,
  count,
  tab,
  on,
  onClick,
}: {
  buttonRef: RefObject<HTMLButtonElement>;
  label: string;
  count: number;
  tab: ShootTab;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="tab"
      id={`shoot-tab-${tab}`}
      aria-selected={on}
      aria-controls={`shoot-panel-${tab}`}
      tabIndex={on ? 0 : -1}
      onClick={onClick}
      className={`
        flex-1 rounded-full py-1.5 font-mono text-[12px] font-semibold tracking-[0.02em]
        transition-colors duration-150
        ${on ? 'bg-card text-fg shadow-flare/30' : 'text-faint'}
      `}
    >
      {label} <span className={on ? 'text-muted' : 'text-faint'}>· {count}</span>
    </button>
  );
}
